import invariant from "tiny-invariant"
import { MediaStreamComposer } from "~/vendor/api.video-typescript-media-stream-composer"
import WaveSurfer from "wavesurfer.js"
import RecordPlugin from "wavesurfer.js/dist/plugins/record"

export class AVController {
  WIDTH = 1280
  HEIGHT = 720
  PIP_WIDTH = 400
  PIP_HEIGHT = 225
  REQUIRED_DISPLAY_SURFACE = "monitor"
  AUDIO_DEVICE_ID_KEY = "audioDeviceId"
  VIDEO_DEVICE_ID_KEY = "videoDeviceId"
  MICROPHONE_TEST_ELEMENT_ID = "audio-capture"
  WAVEFORM_AUDIO_ELEMENT_ID = "waveform-audio-capture"
  WAVEFORM_DISPLAY_ELEMENT_ID = "waveform-display"
  VIDEO_ELEMENT_ID = "video-canvas-container"

  composer: MediaStreamComposer | null = null

  screenShareStreamId: string | null = null
  videoStreamId: string | null = null
  audioStreamId: string | null = null
  audioStream: MediaStream | null = null
  videoStream: MediaStream | null = null
  screenShareStream: MediaStream | null = null
  isRecording = false

  videoDeviceId: string | null = null
  audioDeviceId: string | null = null

  addScreenShareStreamPending = false
  addVideoStreamPending = false
  addAudioStreamPending = false

  microphoneTestEnabled = false

  wavesurfer: WaveSurfer | null = null
  wavesurferRecorder: RecordPlugin | null = null

  private eventListeners: Record<string, Function[]> = {}

  on(event: string, callback: Function) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = []
    }
    this.eventListeners[event].push(callback)
  }

  off(event: string, callback: Function) {
    if (!this.eventListeners[event]) return
    this.eventListeners[event] = this.eventListeners[event].filter(
      (cb) => cb !== callback
    )
  }

  private emitEvent(event: string) {
    if (!this.eventListeners[event]) return
    this.eventListeners[event].forEach((callback) => callback())
  }

  get screenShareEnabled() {
    return !!this.screenShareStreamId
  }

  get screenShareWholeScreen() {
    return (
      this.screenShareStream?.getVideoTracks()[0].getSettings()
        .displaySurface === this.REQUIRED_DISPLAY_SURFACE
    )
  }

  get videoEnabled() {
    return !!this.videoStreamId
  }

  get audioEnabled() {
    return !!this.audioStreamId
  }

  constructor() {
    this.initStoredVideoDevice(localStorage.getItem(this.VIDEO_DEVICE_ID_KEY))
    this.initStoredAudioDevice(localStorage.getItem(this.AUDIO_DEVICE_ID_KEY))

    this.initializeComposer()

    navigator.mediaDevices.addEventListener("devicechange", async () => {
      if (this.videoEnabled) {
        const devices = await this.fetchDevices()
        const videoDevices = devices.filter((d) => d.kind === "videoinput")
        if (videoDevices.length === 0) {
          console.log("Video devices no longer accessible")
          this.disableVideo()
        }
      }
    })
  }

  initializeComposer = () => {
    this.composer = new MediaStreamComposer({
      resolution: {
        width: this.WIDTH,
        height: this.HEIGHT,
      },
    })
  }

  fetchDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter(
      (device) => device.label.length > 0 && device.deviceId.length > 0
    )
  }

  isDeviceAvailable = async (deviceId: string) => {
    const devices = await this.fetchDevices()
    return devices.find((device) => device.deviceId === deviceId)
  }

  initStoredVideoDevice = async (deviceId: string | null) => {
    if (!deviceId) return
    if (await this.isDeviceAvailable(deviceId)) {
      this.videoDeviceId = deviceId
    }
  }

  initStoredAudioDevice = async (deviceId: string | null) => {
    if (!deviceId) return
    if (await this.isDeviceAvailable(deviceId)) {
      this.audioDeviceId = deviceId
    }
  }

  enableScreenShare = async () => {
    try {
      if (!this.composer) this.initializeComposer()
      this.addScreenShareStreamPending = true
      if (this.screenShareStreamId || this.screenShareStream) {
        this.disableScreenShare()
      }
      const screenShareStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: this.REQUIRED_DISPLAY_SURFACE,
        },
      })

      // Add screen share as the background layer
      this.screenShareStreamId = await this.composer!.addStream(
        screenShareStream,
        {
          name: "screen",
          position: "cover",
          mute: true,
          index: 1,
        }
      )

      // Check if the user selected the entire screen
      const displaySurface = screenShareStream
        .getVideoTracks()[0]
        .getSettings().displaySurface

      if (displaySurface !== this.REQUIRED_DISPLAY_SURFACE) {
        this.disableScreenShare()
        this.disableVideo()
        throw new Error("User did not select the entire screen")
      }

      // Add video stream as the PIP layer
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: this.videoDeviceId ? { deviceId: this.videoDeviceId } : true,
      })

      // Calculate the PIP height based on the video stream's aspect ratio
      // Default to 16:9 if the aspect ratio cannot be determined
      const videoTrack = videoStream.getVideoTracks()[0]
      const { width, height } = videoTrack.getSettings()

      let pipWidth = this.PIP_WIDTH
      let pipHeight: number

      if (width && height) {
        const aspectRatio = width / height

        if (Math.abs(aspectRatio - 1.33) < Math.abs(aspectRatio - 1.77)) {
          pipHeight = Math.round(pipWidth / 1.33)
          console.log("Using 4:3 aspect ratio for PIP")
        } else {
          pipHeight = Math.round(pipWidth / 1.77)
          console.log("Using 16:9 aspect ratio for PIP")
        }
      } else {
        pipHeight = this.PIP_HEIGHT
      }

      const pipXPosition = this.WIDTH - pipWidth
      const pipYPosition = this.HEIGHT - pipHeight

      this.videoStreamId = await this.composer!.addStream(videoStream, {
        name: "video",
        position: "fixed",
        x: pipXPosition,
        y: pipYPosition,
        width: this.PIP_WIDTH,
        height: pipHeight,
        draggable: false,
        resizable: false,
        index: 2,
      })

      screenShareStream.getVideoTracks()[0].addEventListener("ended", () => {
        console.log("screen share stream removed")
        this.disableScreenShare()
        this.disableVideo()
      })

      videoStream.getVideoTracks()[0].addEventListener("ended", () => {
        console.log("video stream removed")
        this.disableScreenShare()
        this.disableVideo()
      })

      this.configureCanvas()
    } catch (e) {
      this.screenShareStreamId = null
      throw new Error("Screen share permissions denied")
    } finally {
      this.addScreenShareStreamPending = false
    }
  }

  disableScreenShare = async () => {
    console.log("disabling screen share")
    if (!this.screenShareStreamId) return

    this.composer?.removeStream(this.screenShareStreamId)
    this.screenShareStreamId = null

    if (this.videoStreamId) {
      this.composer?.removeStream(this.videoStreamId)
      this.videoStreamId = null
    }
  }

  enableVideo = async () => {
    console.log("enabling video")
    if (this.addVideoStreamPending) return
    if (this.videoEnabled) return

    try {
      if (!this.composer) this.initializeComposer()
      this.addVideoStreamPending = true
      const stream = await navigator.mediaDevices.getUserMedia({
        video: this.videoDeviceId ? { deviceId: this.videoDeviceId } : true,
      })
      this.videoStreamId = await this.composer!.addStream(stream, {
        position: "cover",
      })
      this.fetchDevices()
      this.configureCanvas()
    } catch (e) {
      this.videoStreamId = null
      throw new Error("Video permissions denied")
    } finally {
      this.addVideoStreamPending = false
    }
  }

  disableVideo = async () => {
    console.log("disabling video")
    this.emitEvent("videoDisabled")
    if (!this.videoStreamId) return

    this.composer?.removeStream(this.videoStreamId)
    this.videoStreamId = null
  }

  enableAudio = async () => {
    console.log("enabling audio")
    if (this.addAudioStreamPending) return
    if (this.audioEnabled) return

    try {
      if (!this.composer) this.initializeComposer()
      this.addAudioStreamPending = true
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: this.audioDeviceId ? { deviceId: this.audioDeviceId } : true,
      })
      this.audioStreamId = this.composer!.addAudioSource(this.audioStream)
      this.fetchDevices()
    } catch (e) {
      this.audioStreamId = null
      throw new Error("Audio permissions denied")
    } finally {
      this.addAudioStreamPending = false
    }
  }

  disableAudio = async () => {
    console.log("disabling audio")
    if (!this.audioStreamId) return
    this.disableMicrophoneTest()
    this.composer?.removeAudioSource(this.audioStreamId)
    this.audioStreamId = null
    this.audioStream = null
  }

  enableWaveform = () => {
    if (!this.wavesurfer) {
      this.wavesurfer = WaveSurfer.create({
        container: document.getElementById(this.WAVEFORM_DISPLAY_ELEMENT_ID)!,
        waveColor: "#3783DB",
        backend: "MediaElement",
        barWidth: 6,
        barRadius: 6,
        barHeight: 1.5,
        barGap: 2,
      })
    }
    if (!this.wavesurferRecorder) {
      this.wavesurferRecorder = this.wavesurfer.registerPlugin(
        RecordPlugin.create()
      )
    }
    this.wavesurferRecorder.startRecording()

    console.log("enabling wavesurfer")
  }

  disableWaveform = () => {
    console.log("disabling wavesurfer")

    if (this.wavesurferRecorder) {
      this.wavesurferRecorder.stopRecording()
      this.wavesurferRecorder.destroy()
      this.wavesurferRecorder = null
    }
    if (this.wavesurfer) {
      this.wavesurfer.destroy()
      this.wavesurfer = null
    }
  }

  enableMicrophoneTest = () => {
    if (!this.audioEnabled) return

    var audioElement = document.getElementById(
      this.MICROPHONE_TEST_ELEMENT_ID
    ) as HTMLAudioElement
    invariant(audioElement)

    audioElement.srcObject = this.audioStream
    audioElement.play()

    this.microphoneTestEnabled = true
  }

  disableMicrophoneTest = () => {
    var audioElement = document.getElementById(
      this.MICROPHONE_TEST_ELEMENT_ID
    ) as HTMLAudioElement
    if (!audioElement) return
    audioElement.srcObject = null
    this.microphoneTestEnabled = false
  }

  setAudioDevice = async (deviceId: string | null) => {
    const deviceWasEnabled = this.audioEnabled
    this.disableMicrophoneTest()
    await this.disableAudio()

    this.audioDeviceId = deviceId
    if (deviceId) {
      localStorage.setItem(this.AUDIO_DEVICE_ID_KEY, deviceId)
    } else {
      localStorage.removeItem(this.AUDIO_DEVICE_ID_KEY)
    }

    if (deviceWasEnabled) {
      await this.enableAudio()
    }
  }

  setVideoDevice = async (deviceId: string | null) => {
    const deviceWasEnabled = this.videoEnabled
    const wasScreenSharing = this.screenShareEnabled

    if (wasScreenSharing) {
      if (this.videoStreamId) {
        this.composer?.removeStream(this.videoStreamId)
        this.videoStreamId = null
      }
    } else {
      await this.disableVideo()
    }

    this.videoDeviceId = deviceId
    if (deviceId) {
      localStorage.setItem(this.VIDEO_DEVICE_ID_KEY, deviceId)
    } else {
      localStorage.removeItem(this.VIDEO_DEVICE_ID_KEY)
    }

    if (deviceWasEnabled) {
      if (wasScreenSharing) {
        try {
          // Only get new video feed for PIP without disturbing screen share
          const videoStream = await navigator.mediaDevices.getUserMedia({
            video: this.videoDeviceId ? { deviceId: this.videoDeviceId } : true,
          })

          const videoTrack = videoStream.getVideoTracks()[0]
          const { width, height } = videoTrack.getSettings()
          let pipWidth = this.PIP_WIDTH
          let pipHeight: number

          if (width && height) {
            const aspectRatio = width / height
            if (Math.abs(aspectRatio - 1.33) < Math.abs(aspectRatio - 1.77)) {
              pipHeight = Math.round(pipWidth / 1.33)
            } else {
              pipHeight = Math.round(pipWidth / 1.77)
            }
          } else {
            pipHeight = this.PIP_HEIGHT
          }

          const pipXPosition = this.WIDTH - pipWidth
          const pipYPosition = this.HEIGHT - pipHeight

          // Add the new video as PIP
          this.videoStreamId = await this.composer!.addStream(videoStream, {
            name: "video",
            position: "fixed",
            x: pipXPosition,
            y: pipYPosition,
            width: this.PIP_WIDTH,
            height: pipHeight,
            draggable: false,
            resizable: false,
            index: 2,
          })

          videoStream.getVideoTracks()[0].addEventListener("ended", () => {
            console.log("video stream removed")
            this.disableScreenShare()
            this.disableVideo()
          })
        } catch (e) {
          console.error("Failed to update video in screen share mode:", e)
          await this.disableScreenShare()
          await this.enableScreenShare()
        }
      } else {
        await this.enableVideo()
      }
    }
  }

  startRecording = async (videoId: string) => {
    if (this.isRecording) return
    if (!this.composer) this.initializeComposer()

    this.composer!.startRecording({
      uploadToken: "fake-but-necessary",
      videoId,
    })
    console.log("started recording")
    this.isRecording = true
  }
  stopRecording = async () => {
    if (!this.isRecording) return
    if (!this.composer) this.initializeComposer()

    console.log("stopping recording...")
    await this.composer!.stopRecording()
    console.log("recording complete")
    this.isRecording = false
  }

  configureCanvas = () => {
    // Try to initialize the composer if it hasn't been initialized yet
    if (!this.composer) this.initializeComposer()

    // If the composer is still null, return
    if (!this.composer) return

    try {
      this.composer.appendCanvasTo(`#${this.VIDEO_ELEMENT_ID}`)
      const canvas = this.composer.getCanvas()
      canvas!.style.width = "100%"
      canvas!.style.boxSizing = "unset"
    } catch (e) {
      console.warn(e)
    }
  }

  cleanup = () => {
    if (!this.composer) return
    this.disableScreenShare()
    this.disableVideo()
    this.disableAudio()
    this.disableWaveform()
    this.composer.destroy()
    this.composer = null
  }
}
